import type {
  DocumentChangedEvent,
  DocumentSavedEvent,
  FileChangedEvent,
} from "@magic-md/engine-client/protocol";
import { getDocumentBundleDir } from "../lib/publish-utils";
import { frontendLog } from "../utils/frontend-logger";
import { useEditorStore } from "./editor-store";
import type { NotificationBus } from "./notification-bus";
import { sessionRevisionTracker } from "./session-revision-tracker";
import { tabSessionRegistry } from "./tab-session-registry";

export interface DocumentNotificationHandlerDependencies {
  reconcileEngineTabContent: typeof import("./plugin-execution-store").reconcileEngineTabContent;
  resyncExternalConfigFile: typeof import("./plugin-execution-store").resyncExternalConfigFile;
}

function refreshExternalConfigSession(
  dependencies: DocumentNotificationHandlerDependencies,
  filePath: string,
): void {
  void dependencies.resyncExternalConfigFile(filePath).catch((error) => {
    frontendLog.warn(
      `[external-config] Failed to refresh session for ${filePath}: ${error instanceof Error ? error.message : String(error)}`,
      undefined,
      { source: "engine" },
    );
  });
}

function notifyDocumentBundleRefresh(filePath: string | undefined): void {
  if (!filePath || !/\.md$/i.test(filePath)) return;
  const bundleDir = getDocumentBundleDir(filePath);
  useEditorStore
    .getState()
    .refreshSubtree(bundleDir)
    .catch(() => {});
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("magic-md:bundle-refresh", {
      detail: { filePath, bundleDir },
    }),
  );
}

export function registerDocumentNotificationHandlers(
  bus: NotificationBus,
  dependencies: DocumentNotificationHandlerDependencies,
): () => void {
  const disposers: Array<() => void> = [];
  const registerDomain = (
    prefix: string,
    handler: Parameters<NotificationBus["registerDomain"]>[1],
  ) => {
    disposers.push(bus.registerDomain(prefix, handler));
  };

  registerDomain("event/document", (method, params) => {
    if (method === "event/document.changed") {
      const event = params as DocumentChangedEvent;
      if (!sessionRevisionTracker.accept(event.sessionId, event.revision)) {
        frontendLog.debug(
          `[document.changed] Ignored stale revision: session=${event.sessionId} revision=${event.revision}`,
          undefined,
          { source: "engine" },
        );
        return;
      }

      const editorState = useEditorStore.getState();
      const targetTabIds: string[] = [];
      if (event.filePath) {
        for (const tab of editorState.tabs) {
          if (tab.filePath === event.filePath) targetTabIds.push(tab.id);
        }
      }

      if (targetTabIds.length === 0) {
        for (const { tabId, sessionId } of tabSessionRegistry.entries()) {
          if (sessionId !== event.sessionId) continue;
          const tab = editorState.tabs.find((entry) => entry.id === tabId);
          if (
            event.filePath &&
            tab?.filePath &&
            tab.filePath !== event.filePath
          ) {
            frontendLog.warn(
              `[document.changed] Stale session mapping: tab=${tabId} (${tab.title}) filePath=${tab.filePath} ≠ event.filePath=${event.filePath} — skipping`,
              undefined,
              { source: "engine" },
            );
            continue;
          }
          targetTabIds.push(tabId);
        }
      }

      if (targetTabIds.length === 0) {
        frontendLog.debug(
          `[document.changed] Dropped: session=${event.sessionId} filePath=${event.filePath ?? "none"} — no matching tab`,
          undefined,
          { source: "engine" },
        );
        return;
      }

      const bundleRefreshFilePath =
        event.filePath ??
        targetTabIds
          .map(
            (tabId) =>
              editorState.tabs.find((tab) => tab.id === tabId)?.filePath,
          )
          .find((filePath): filePath is string => !!filePath);
      notifyDocumentBundleRefresh(bundleRefreshFilePath);

      for (const targetTabId of targetTabIds) {
        const tab = editorState.tabs.find((entry) => entry.id === targetTabId);
        if (!tab) continue;
        dependencies.reconcileEngineTabContent({
          tabId: targetTabId,
          sessionId: event.sessionId,
          tabTitle: tab.title,
          logPrefix: "document.changed",
          filePath: event.filePath,
          fallbackContent: tab.content,
          content: event.content,
          presentationContent: event.presentationContent,
          cancelPendingSync: true,
          preserveLocalChanges: true,
        });
      }
      return;
    }

    if (method === "event/document.saved") {
      const event = params as DocumentSavedEvent;
      if (!event.filePath) return;
      frontendLog.debug(
        `[document.saved] ${event.filePath} rev=${event.revision}`,
        undefined,
        { source: "engine" },
      );
      const matchingTabIds = useEditorStore
        .getState()
        .tabs.filter((tab) => tab.filePath === event.filePath)
        .map((tab) => tab.id);
      if (matchingTabIds.length === 0) return;
      useEditorStore.setState((current) => ({
        tabs: current.tabs.map((tab) =>
          matchingTabIds.includes(tab.id) ? { ...tab, isDirty: false } : tab,
        ),
      }));
    }
  });

  registerDomain("event/file", (method, params) => {
    if (method !== "event/file.changed") return;
    const event = params as FileChangedEvent;
    frontendLog.debug(
      `[file-event] ${event.changeType}: ${event.filePath}`,
      undefined,
      { source: "system" },
    );
    refreshExternalConfigSession(dependencies, event.filePath);
    if (event.changeType === "changed") {
      useEditorStore.getState()._handleExternalFileChange(event.filePath);
    }
  });

  registerDomain("event/workspace", (method, params) => {
    if (method === "event/workspace.foldersChanged") {
      const event =
        params as import("@magic-md/engine-client/protocol").WorkspaceFoldersChangedEvent;
      useEditorStore.setState({
        workspaceFolders: event.folders,
        workspacePath: event.folders[0]?.path ?? null,
      });
      useEditorStore.getState().refreshFileTree();
    } else if (method === "event/workspace.fileChanged") {
      const event =
        params as import("@magic-md/engine-client/protocol").WorkspaceFileChangedEvent;
      refreshExternalConfigSession(dependencies, event.filePath);
      if (event.changeType === "changed") {
        useEditorStore.getState()._handleExternalFileChange(event.filePath);
      } else {
        const parentDir = event.filePath.substring(
          0,
          event.filePath.lastIndexOf("/"),
        );
        if (parentDir) useEditorStore.getState().refreshSubtree(parentDir);
      }
    } else if (method === "event/workspace.starsChanged") {
      const event =
        params as import("@magic-md/engine-client/protocol").WorkspaceStarsChangedEvent;
      useEditorStore.getState()._setStarred(event.items ?? []);
    } else if (method === "event/workspace.bookmarksChanged") {
      const event =
        params as import("@magic-md/engine-client/protocol").WorkspaceBookmarksChangedEvent;
      void import("./bookmark-store").then(({ useBookmarkStore }) => {
        useBookmarkStore.getState()._setBookmarks(event.items ?? []);
      });
    }
  });

  registerDomain("event/history", (method, params) => {
    void import("./history-store").then(({ useHistoryStore }) => {
      const store = useHistoryStore.getState();
      if (method === "event/history.snapshotCreated") {
        const event =
          params as import("@magic-md/engine-client/protocol").HistorySnapshotCreatedEvent;
        store._upsertSnapshot(event.filePath, event.snapshot);
      } else if (method === "event/history.restored") {
        const event =
          params as import("@magic-md/engine-client/protocol").HistoryRestoredEvent;
        void store.refresh(event.filePath);
      } else if (method === "event/history.metaChanged") {
        const event =
          params as import("@magic-md/engine-client/protocol").HistoryMetaChangedEvent;
        void store.refresh(event.filePath);
      }
    });
  });

  disposers.push(
    bus.subscribe("event/document.activity", (params) => {
      const event =
        params as import("@magic-md/engine-client/protocol").DocumentActivityEvent;
      useEditorStore.getState().handleDocumentActivity(event);
    }),
  );

  return () => {
    for (const dispose of disposers.splice(0)) dispose();
  };
}
